import { useEffect, useMemo, useState } from "react"
import { supabase } from "../supabaseClient"

import JobCard from "../components/time-tracker/JobCard"
import NewJobModal from "../components/time-tracker/NewJobModal"
import ClockScreen from "../components/time-tracker/ClockScreen"
import NewSessionModal from "../components/time-tracker/NewSessionModal"
import SessionHistory from "../components/time-tracker/SessionHistory"

interface WorkTask {
  id: string
  task_name: string
  client_name: string | null
  company: string | null
  hourly_rate: number
  is_active: boolean
}

interface ActiveSession {
  id: string
  task_id: string
  start_time: string
  status: "working"
}

interface ActiveBreak {
  id: string
  start_time: string
}

interface SessionBreakRow {
  id: string
  session_id: string
  start_time: string
  end_time: string | null
}

type Screen = "list" | "clock" | "history"

export default function TimeTracker() {
  const [jobs, setJobs] = useState<WorkTask[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [isNewJobOpen, setIsNewJobOpen] = useState(false)
  const [activeSession, setActiveSession] = useState<ActiveSession | null>(null)

  const [activeBreak, setActiveBreak] = useState<ActiveBreak | null>(null)
  const [isOnBreak, setIsOnBreak] = useState(false)

  // ✅ breaks de la sesión activa (para earnings live)
  const [sessionBreaks, setSessionBreaks] = useState<SessionBreakRow[]>([])

  const [customClockJobId, setCustomClockJobId] = useState<string | null>(null)
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0)

  // Navigation: list (jobs) -> clock (selected job) -> history (that job's sessions)
  const [screen, setScreen] = useState<Screen>("list")
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null)

  /* -----------------------------------
     Load Jobs
  ----------------------------------- */
  const loadJobs = async () => {
    // Fetch ALL jobs (including soft-deleted/is_active=false) so an in-progress
    // session on a deleted job can still resolve its name/rate — only the
    // visible list (see `visibleJobs` below) is filtered to active jobs.
    const { data, error } = await supabase
      .from("work_tasks")
      .select("*")
      .order("created_at", { ascending: false })

    if (error) {
      setError(error.message)
      return
    }

    setJobs(data || [])
  }

  /* -----------------------------------
     Load Active Session + Breaks
  ----------------------------------- */
  const loadActiveSession = async () => {
    const { data: session } = await supabase
      .from("work_sessions")
      .select("id, task_id, start_time, status")
      .eq("status", "working")
      .single()

    if (!session) {
      setActiveSession(null)
      setActiveBreak(null)
      setIsOnBreak(false)
      setSessionBreaks([])
      return
    }

    setActiveSession(session)

    // ✅ trae TODOS los breaks de esa sesión
    const { data: allBreaks, error: breaksError } = await supabase
      .from("work_breaks")
      .select("id, session_id, start_time, end_time")
      .eq("session_id", session.id)
      .order("start_time", { ascending: true })

    if (!breaksError) {
      setSessionBreaks((allBreaks as SessionBreakRow[]) || [])
      const active = (allBreaks || []).find((b: any) => b.end_time === null)
      if (active) {
        setActiveBreak({ id: active.id, start_time: active.start_time })
        setIsOnBreak(true)
      } else {
        setActiveBreak(null)
        setIsOnBreak(false)
      }
    } else {
      // si por alguna razón falla, no rompemos UI
      setSessionBreaks([])
      setActiveBreak(null)
      setIsOnBreak(false)
    }

    return session
  }

  /* -----------------------------------
     Initial Load
  ----------------------------------- */
  useEffect(() => {
    const init = async () => {
      setLoading(true)
      const [, session] = await Promise.all([loadJobs(), loadActiveSession()])
      // Land back on the clock screen if a session was already running
      // (e.g. page reload while clocked in).
      if (session) {
        setSelectedJobId(session.task_id)
        setScreen("clock")
      }
      setLoading(false)
    }
    init()
  }, [])

  /* -----------------------------------
     Derived
  ----------------------------------- */
  const visibleJobs = useMemo(() => jobs.filter((j) => j.is_active), [jobs])

  const selectedJob = useMemo(
    () => jobs.find((j) => j.id === selectedJobId) || null,
    [jobs, selectedJobId]
  )

  const isSelectedJobActive = !!activeSession && activeSession.task_id === selectedJobId

  /* -----------------------------------
     Navigation
  ----------------------------------- */
  const handleSelectJob = (job: WorkTask) => {
    if (activeSession && activeSession.task_id !== job.id) return
    setSelectedJobId(job.id)
    setScreen("clock")
  }

  const handleBackToList = () => {
    setScreen("list")
    setSelectedJobId(null)
  }

  const handleOpenHistory = () => setScreen("history")
  const handleBackToClock = () => setScreen("clock")

  /* -----------------------------------
     Clock-In (Now)
  ----------------------------------- */
  const handleClockIn = async (job: WorkTask) => {
    if (activeSession) {
      alert("You already have an active session.")
      return
    }

    const { error } = await supabase.from("work_sessions").insert([
      {
        task_id: job.id,
        start_time: new Date().toISOString(),
        status: "working",
      },
    ])

    if (error) {
      alert("Clock-in failed: " + error.message)
      return
    }

    await loadActiveSession()
  }

  /* -----------------------------------
     Clock-In (Custom Time)
  ----------------------------------- */
  const handleClockInCustom = (jobId: string) => {
    if (activeSession) {
      alert("You already have an active session.")
      return
    }
    setCustomClockJobId(jobId)
  }

  /* -----------------------------------
     Delete Job (soft delete)
  ----------------------------------- */
  const handleDeleteJob = async (job: WorkTask) => {
    if (activeSession?.task_id === job.id) {
      alert("You can't delete a job with an active session. Clock out first.")
      return
    }

    if (!window.confirm(`Delete "${job.task_name}"? Past sessions and reports will be kept.`)) return

    const { error } = await supabase
      .from("work_tasks")
      .update({ is_active: false })
      .eq("id", job.id)

    if (error) {
      alert("Delete failed: " + error.message)
      return
    }

    setJobs((prev) => prev.map((j) => (j.id === job.id ? { ...j, is_active: false } : j)))
  }

  /* -----------------------------------
     Pause / Resume
  ----------------------------------- */
  const handlePause = async () => {
    if (!activeSession) return
    if (isOnBreak) return

    const nowIso = new Date().toISOString()

    const { data, error } = await supabase
      .from("work_breaks")
      .insert({
        session_id: activeSession.id,
        start_time: nowIso,
      })
      .select("id, session_id, start_time, end_time")
      .single()

    if (error) {
      alert("Pause failed: " + error.message)
      return
    }

    const newBreak = data as SessionBreakRow
    setSessionBreaks((prev) => [...prev, newBreak])
    setActiveBreak({ id: newBreak.id, start_time: newBreak.start_time })
    setIsOnBreak(true)
  }

  const handleResume = async () => {
    if (!activeSession) return
    if (!isOnBreak || !activeBreak) return

    const endIso = new Date().toISOString()

    const { error } = await supabase
      .from("work_breaks")
      .update({ end_time: endIso })
      .eq("id", activeBreak.id)

    if (error) {
      alert("Resume failed: " + error.message)
      return
    }

    // actualiza estado local
    setSessionBreaks((prev) =>
      prev.map((b) => (b.id === activeBreak.id ? { ...b, end_time: endIso } : b))
    )
    setActiveBreak(null)
    setIsOnBreak(false)
  }

  /* -----------------------------------
     Edit Clock-in Time (active session)
  ----------------------------------- */
  const handleEditStartTime = async (newStartIso: string) => {
    if (!activeSession) return

    const { error } = await supabase
      .from("work_sessions")
      .update({
        start_time: newStartIso,
        is_manually_edited: true,
        updated_at: new Date().toISOString(),
      })
      .eq("id", activeSession.id)

    if (error) {
      alert("Failed to update clock-in time: " + error.message)
      return
    }

    setActiveSession((prev) => (prev ? { ...prev, start_time: newStartIso } : prev))
  }

  /* -----------------------------------
     LIVE: totalBreakSeconds (closed + active running)
  ----------------------------------- */
  const totalBreakSeconds = useMemo(() => {
    if (!activeSession) return 0

    let seconds = 0

    // breaks cerrados
    for (const b of sessionBreaks) {
      if (b.end_time) {
        seconds +=
          (new Date(b.end_time).getTime() - new Date(b.start_time).getTime()) / 1000
      }
    }

    // break activo (en curso)
    if (isOnBreak && activeBreak) {
      seconds += (Date.now() - new Date(activeBreak.start_time).getTime()) / 1000
    }

    return Math.max(0, Math.floor(seconds))
  }, [activeSession, sessionBreaks, isOnBreak, activeBreak])

  /* -----------------------------------
     Clock-Out (with break discount)
  ----------------------------------- */
  const handleClockOut = async () => {
    if (!activeSession) return

    const start = new Date(activeSession.start_time)
    const end = new Date()

    // Para precisión (multi-tab), vuelve a traer breaks del server
    const { data: breaksFromDb } = await supabase
      .from("work_breaks")
      .select("start_time, end_time")
      .eq("session_id", activeSession.id)

    let breakSeconds = 0
    ;(breaksFromDb || []).forEach((b: { start_time: string; end_time: string | null }) => {
      if (b.end_time) {
        breakSeconds +=
          (new Date(b.end_time).getTime() - new Date(b.start_time).getTime()) / 1000
      }
    })

    const workedSeconds = (end.getTime() - start.getTime()) / 1000 - breakSeconds
    const durationMins = Math.max(0, Math.floor(workedSeconds / 60))

    const { error } = await supabase
      .from("work_sessions")
      .update({
        end_time: end.toISOString(),
        duration_mins: durationMins,
        status: "completed",
      })
      .eq("id", activeSession.id)

    if (error) {
      alert("Clock-out failed: " + error.message)
      return
    }

    setActiveSession(null)
    setActiveBreak(null)
    setIsOnBreak(false)
    setSessionBreaks([])
    setHistoryRefreshKey((k) => k + 1)
    handleBackToList()
  }

  /* -----------------------------------
     Render
  ----------------------------------- */
  if (screen === "clock" && selectedJob) {
    return (
      <>
        <ClockScreen
          jobName={selectedJob.task_name}
          hourlyRate={selectedJob.hourly_rate}
          isActive={isSelectedJobActive}
          startTime={isSelectedJobActive ? activeSession!.start_time : null}
          isOnBreak={isOnBreak}
          totalBreakSeconds={totalBreakSeconds}
          onBack={handleBackToList}
          onOpenHistory={handleOpenHistory}
          onClockIn={() => handleClockIn(selectedJob)}
          onClockInCustom={() => handleClockInCustom(selectedJob.id)}
          onPause={handlePause}
          onResume={handleResume}
          onClockOut={handleClockOut}
          onEditStartTime={handleEditStartTime}
        />

        <NewSessionModal
          open={!!customClockJobId}
          jobId={customClockJobId}
          onClose={async () => {
            setCustomClockJobId(null)
            await loadActiveSession()
          }}
        />
      </>
    )
  }

  if (screen === "history") {
    return (
      <div className="mx-auto max-w-6xl px-4 py-6 text-black dark:text-white">
        <SessionHistory
          refreshKey={historyRefreshKey}
          initialJobFilter={selectedJobId || "all"}
          onBack={handleBackToClock}
        />
      </div>
    )
  }

  return (
    <>
      <div className="mx-auto max-w-6xl px-4 py-6 text-black dark:text-white">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-xl font-semibold">Time Tracker</h1>
            <p className="text-sm text-gray-500">Select a job to clock in or out.</p>
          </div>

          <button
            onClick={() => setIsNewJobOpen(true)}
            className="w-full sm:w-auto bg-[#2563EB] text-white px-4 py-3 sm:py-2 rounded-lg text-sm font-medium hover:bg-[#1d4ed8] transition"
          >
            New Job
          </button>
        </div>

        {error && <div className="mb-4 text-sm text-red-600">{error}</div>}

        {loading ? (
          <div className="text-sm text-gray-500 dark:text-gray-400">Loading jobs...</div>
        ) : visibleJobs.length === 0 ? (
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-10 text-center bg-white dark:bg-neutral-900">
            <p className="font-medium">No jobs yet</p>
            <p className="text-sm text-gray-500 mt-1">Start by creating your first job.</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {visibleJobs.map((job) => (
              <JobCard
                key={job.id}
                job={job}
                onSelect={handleSelectJob}
                onDelete={handleDeleteJob}
                isActive={activeSession?.task_id === job.id}
                disabled={!!activeSession && activeSession.task_id !== job.id}
              />
            ))}
          </div>
        )}
      </div>

      <NewJobModal
        isOpen={isNewJobOpen}
        onClose={() => setIsNewJobOpen(false)}
        onSuccess={(job) => setJobs((prev) => [job, ...prev])}
        onError={(msg) => alert(msg)}
      />
    </>
  )
}
